import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button } from '../../form/Button';
import FormTitle from '../../form/FormTitle';
import AMSSelect from '../../AMSSelect';
import { useTheme } from '../../../middleware/ThemeContext';
import { Field, FieldConfig, FieldDataTypeEnum, FieldType, TempSettings } from '../../../types/types';
import { Theme } from '../../../utils/themes';
import StyledText from '../../StyledComponents/StyledText';
import LeftAlignIcon from '../../../assets/icons/LeftAlignIcon';
import CenterAlignIcon from '../../../assets/icons/CenterAlignIcon';
import RightAlignIcon from '../../../assets/icons/RightAlignIcon';
import CrossIcon from '../../../assets/icons/CrossIcon';
import SaveIcon from '../../../assets/icons/SaveIcon';
// Services & Utils
import { CreateFormTheme } from "../../../utils/themes";
import { Style } from '@mui/icons-material';
import StyledInput from '../../StyledComponents/StyledInput';
import StyledRadio from '../../StyledComponents/StyledRadio';
import StyledTextEditor from '../../StyledComponents/TextEditor/StyledTextEditor';

interface FieldSettingsPanelProps {
  isOpen: boolean;
  field: Field | null;
  onClose: () => void;
  onSave: (config: FieldConfig) => void;
}

const DEFAULT_HEADING_TEXT = "Let's Create Something Amazing Together";
const DEFAULT_PARAGRAPH_TEXT = "Let's Create Something Amazing Together";

const HEADING_FONT_SIZE_MAP: Record<string, number> = {
  H1: 32,
  H2: 24,
  H3: 19,
  H4: 16,
  H5: 13,
  H6: 10,
};

const getMode = (field?: Field | null): 'heading' | 'paragraph' | 'divider' => {
  const fieldType = String(field?.fieldType || '').toUpperCase() as FieldType | '';
  if (fieldType === 'PARAGRAPH') return 'paragraph';
  if (fieldType === 'DIVIDER') return 'divider';
  return 'heading';
};

const stripPx = (v?: any): number | '' => {
  if (v === undefined || v === null) return '';
  const s = String(v).trim();
  const m = s.match(/^(-?\d+)(?:px)?$/i);
  if (m) return parseInt(m[1], 10);
  const num = parseInt(s, 10);
  return Number.isNaN(num) ? '' : num;
};

const getHeadingDefaults = (uiMeta: FieldConfig['uiMeta']) => ({
  hideField: false,
  hideLabel: true,
  isRequired: false,
  displayValue: uiMeta?.displayValue || DEFAULT_HEADING_TEXT,
  headingLevel: uiMeta?.headingLevel || 'H1',
  textAlignment: uiMeta?.textAlignment || 'left',
  fontSize: stripPx(uiMeta?.fontSize) || HEADING_FONT_SIZE_MAP.H1,
  fontWeight: uiMeta?.fontWeight || 'bold',
  color: uiMeta?.color || '#3B6FF5',
});

type ParagraphUiMeta = FieldConfig['uiMeta'] & {
  htmlContent?: string;
  paragraphContent?: string;
};

const getParagraphDefaults = (uiMeta: ParagraphUiMeta) => ({
  hideField: false,
  hideLabel: true,
  isRequired: false,
  displayValue: uiMeta?.displayValue || uiMeta?.htmlContent || uiMeta?.paragraphContent || DEFAULT_PARAGRAPH_TEXT,
  htmlContent: uiMeta?.htmlContent || uiMeta?.displayValue || uiMeta?.paragraphContent || DEFAULT_PARAGRAPH_TEXT,
  paragraphContent: uiMeta?.paragraphContent || uiMeta?.htmlContent || uiMeta?.displayValue || DEFAULT_PARAGRAPH_TEXT,
  characterLimitEnabled: uiMeta?.characterLimitEnabled ?? Boolean(uiMeta?.maxLength),
  maxLength: uiMeta?.maxLength ?? '',
  paragraphTextAlignment: uiMeta?.paragraphTextAlignment || 'left',
  paragraphFontSize: stripPx(uiMeta?.paragraphFontSize) || 18,
  paragraphFontWeight: uiMeta?.paragraphFontWeight || 'regular',
  paragraphLineHeight: uiMeta?.paragraphLineHeight || '1.6',
  paragraphColor: uiMeta?.paragraphColor || '#3B6FF5',
});

const getDividerDefaults = (uiMeta: FieldConfig['uiMeta']) => ({
  hideField: false,
  hideLabel: true,
  isRequired: false,
  displayValue: uiMeta?.displayValue || '',
  separatorColor: uiMeta?.separatorColor || '#BABDC1',
  separatorHeight: stripPx(uiMeta?.separatorHeight) || 2,
  separatorStyle: uiMeta?.separatorStyle || 'solid',
  separatorWidth: uiMeta?.separatorWidth || '100%',
  separatorSpacing: uiMeta?.separatorSpacing || '16px 0',
});

const getInitialSettings = (field: Field | null): TempSettings => {
  const uiMeta = (field?.config?.uiMeta || {}) as ParagraphUiMeta;
  const mode = getMode(field);

  if (mode === 'paragraph') return getParagraphDefaults(uiMeta);
  if (mode === 'divider') return getDividerDefaults(uiMeta);
  return getHeadingDefaults(uiMeta);
};

const FieldSettingsPanel: React.FC<FieldSettingsPanelProps> = ({ isOpen, field, onClose, onSave }) => {
  const { theme } = useTheme();
  const [tempSettings, setTempSettings] = useState<TempSettings>({});

  const mode = useMemo(() => getMode(field), [field]);
  const fieldTitle = useMemo(() => {
    if (mode === 'paragraph') return 'Paragraph Settings';
    if (mode === 'divider') return 'Divider Settings';
    return 'Heading Settings';
  }, [mode]);

  useEffect(() => {
    if (!isOpen) return;
    setTempSettings(getInitialSettings(field));
  }, [isOpen, field]);

  const updateTempSetting = (key: string, value: any) => {
    setTempSettings(prev => ({ ...prev, [key]: value }));
  };

  const updateHeadingLevel = (level: string) => {
    setTempSettings(prev => {
      const nextSettings = { ...prev, headingLevel: level } as TempSettings;

      if (!prev.headingFontSizeManuallyEdited) {
        nextSettings.fontSize = HEADING_FONT_SIZE_MAP[level] ?? HEADING_FONT_SIZE_MAP.H1;
      }

      return nextSettings;
    });
  };

  const updateHeadingFontSize = (value: string) => {
    setTempSettings(prev => ({
      ...prev,
      fontSize: value === '' ? '' : Number(value),
      headingFontSizeManuallyEdited: true,
    }));
  };

  const handleSave = () => {
    if (!field) return;
    const { headingFontSizeManuallyEdited, ...restTempSettings } = tempSettings;
    const uiMetaPatch: any = {
      ...(field.config?.uiMeta || {}),
      ...restTempSettings,
    };

    // Ensure pixel-based fields are saved with 'px'
    if (tempSettings.fontSize !== undefined && tempSettings.fontSize !== '') {
      uiMetaPatch.fontSize = `${tempSettings.fontSize}px`;
    }
    if (tempSettings.paragraphFontSize !== undefined && tempSettings.paragraphFontSize !== '') {
      uiMetaPatch.paragraphFontSize = `${tempSettings.paragraphFontSize}px`;
    }
    if (tempSettings.maxLength !== undefined) {
      if (tempSettings.maxLength === '' || tempSettings.maxLength === null) {
        uiMetaPatch.characterLimitEnabled = false;
        uiMetaPatch.maxLength = undefined;
      } else {
        uiMetaPatch.characterLimitEnabled = true;
        uiMetaPatch.maxLength = Number(tempSettings.maxLength);
      }
    }
    if (tempSettings.separatorHeight !== undefined && tempSettings.separatorHeight !== '') {
      uiMetaPatch.separatorHeight = `${tempSettings.separatorHeight}px`;
    }

    const nextConfig: FieldConfig = {
      ...(field.config || {}),
      uiMeta: uiMetaPatch,
    };
    onSave(nextConfig);
  };

  const headingLevel = String(tempSettings.headingLevel || 'H1').toUpperCase();
  const headingOptions = ['H1', 'H2', 'H3', 'H4', 'H5', 'H6'];
  const fontWeightOptions = [
    { label: 'Regular', value: 'regular' },
    { label: 'Medium', value: 'medium' },
    { label: 'Bold', value: 'bold' },
  ];
  const separatorTypeOptions = [
    { label: 'Solid', value: 'solid' },
    { label: 'Dashed', value: 'dashed' },
    { label: 'Dotted', value: 'dotted' },
  ];
  const alignOptions = [
    { value: 'left', icon: <LeftAlignIcon />, label: 'Left' },
    { value: 'center', icon: <CenterAlignIcon />, label: 'Center' },
    { value: 'right', icon: <RightAlignIcon />, label: 'Right' },
  ];

  return (
    <>
      <Backdrop isOpen={isOpen} onClick={onClose} />
      <RightSidebar isOpen={isOpen} theme={theme}>
        {isOpen && field && (
          <>
            <SidebarHeaderWrapper>
              <FormTitle title={fieldTitle} />
            </SidebarHeaderWrapper>

            <SidebarContent theme={theme}>
              {mode === 'divider' ? (
                <SectionTitle theme={theme}>Appearance</SectionTitle>
              ) : (
                <SectionTitle theme={theme}>Content</SectionTitle>
              )}

              {mode === 'heading' && (
                <>
                  <FieldBlock>
                    <Label theme={theme}>Select Heading :</Label>
                    <LevelRow>
                      {headingOptions.map((level) => (
                        <LevelButton
                          key={level}
                          theme={theme}
                          $selected={headingLevel === level}
                          type="button"
                          onClick={() => updateHeadingLevel(level)}
                        >
                          {level}
                        </LevelButton>
                      ))}
                    </LevelRow>
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Text</Label>
                    <StyledInput
                      theme={theme}
                      $fontWeight="500"
                      color={theme.CorporateTheme.colors.headerColor}
                      value={tempSettings.displayValue || ''}
                      onChange={(e) => updateTempSetting('displayValue', e.target.value)}
                      placeholder={DEFAULT_HEADING_TEXT}
                      style={{ background: theme.CorporateTheme.colors.background }}
                    />
                  </FieldBlock>
                </>
              )}

              {mode === 'paragraph' && (
                <FieldBlock>
                  <Label theme={theme}>Text</Label>
                  <StyledTextEditor
                    value={tempSettings.htmlContent ?? tempSettings.displayValue ?? tempSettings.paragraphContent ?? ''}
                    onChange={(value) => {
                      updateTempSetting('htmlContent', value);
                      updateTempSetting('displayValue', value);
                      updateTempSetting('paragraphContent', value);
                    }}
                    placeholder={DEFAULT_PARAGRAPH_TEXT}
                    isReadOnly={false}
                    minHeight="220px"
                  />
                </FieldBlock>
              )}

              

              {mode === 'heading' && (
                <>
                  <SectionTitle theme={theme}>Styling</SectionTitle>

                  <FieldBlock>
                    <Label theme={theme}>Text Color</Label>
                    <ColorRow>
                      <ColorInput
                        type="color"
                        value={tempSettings.color || '#3B6FF5'}
                        onChange={(e) => updateTempSetting('color', e.target.value)}
                      />
                      <StyledInput
                        theme={theme}
                        $fontWeight="500"
                        color={theme.CorporateTheme.colors.headerColor}
                        value={tempSettings.color || ''}
                        onChange={(e) => updateTempSetting('color', e.target.value)}
                        placeholder="#3B6FF5"
                        style={{ background: theme.CorporateTheme.colors.background }}
                      />
                    </ColorRow>
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Text Alignment</Label>
                    <AlignmentRow>
                      {alignOptions.map((option) => (
                        <AlignmentOption
                          key={option.value}
                          theme={theme}
                          $selected={(tempSettings.textAlignment || 'left') === option.value}
                          onClick={() => updateTempSetting('textAlignment', option.value)}
                        >
                          <StyledRadio
                            name="headingAlignment"
                            checked={(tempSettings.textAlignment || 'left') === option.value}
                            onChange={() => updateTempSetting('textAlignment', option.value)}
                            label=""
                            radioColor={theme.CorporateTheme.colors.primary}
                            borderWidth="2px"
                          />
                          <AlignmentIconBox>
                            {option.icon}
                          </AlignmentIconBox>
                        </AlignmentOption>
                      ))}
                    </AlignmentRow>
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Font Size</Label>
                    <NumberPxWrapper>
                      <StyledInput
                        theme={theme}
                        $fontWeight="500"
                        color={theme.CorporateTheme.colors.headerColor}
                        type="number"
                        value={tempSettings.fontSize ?? ''}
                        onChange={(e) => updateHeadingFontSize(e.target.value)}
                        placeholder="48"
                        min={0}
                        style={{ background: theme.CorporateTheme.colors.background }}
                      />
                      <PxBadge>PX</PxBadge>
                    </NumberPxWrapper>
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Font Weight</Label>
                    <AMSSelect
                      staticOptions={fontWeightOptions}
                      value={fontWeightOptions.find(option => option.value === (tempSettings.fontWeight || 'bold')) || fontWeightOptions[2]}
                      onChange={(option) => {
                        const selectedOption = Array.isArray(option) ? option[0] : option;
                        if (selectedOption?.value) {
                          updateTempSetting('fontWeight', selectedOption.value);
                        }
                      }}
                      placeholder="Select font weight"
                      isSearchable={false}
                      isClearable={false}
                    />
                  </FieldBlock>
                </>
              )}

              {mode === 'paragraph' && (
                <>
                  <SectionTitle theme={theme}>Styling</SectionTitle>

                  <FieldBlock>
                    <Label theme={theme}>Text Color</Label>
                    <ColorRow>
                      <ColorInput
                        type="color"
                        value={tempSettings.paragraphColor || '#3B6FF5'}
                        onChange={(e) => updateTempSetting('paragraphColor', e.target.value)}
                      />
                      <StyledInput
                        theme={theme}
                        $fontWeight="500"
                        color={theme.CorporateTheme.colors.headerColor}
                        value={tempSettings.paragraphColor || ''}
                        onChange={(e) => updateTempSetting('paragraphColor', e.target.value)}
                        placeholder="#3B6FF5"
                        style={{ background: theme.CorporateTheme.colors.background }}
                      />
                    </ColorRow>
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Font Size</Label>
                    <NumberPxWrapper>
                      <StyledInput
                        theme={theme}
                        $fontWeight="500"
                        color={theme.CorporateTheme.colors.headerColor}
                        type="number"
                        value={tempSettings.paragraphFontSize ?? ''}
                        onChange={(e) => updateTempSetting('paragraphFontSize', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="18"
                        min={0}
                        style={{ background: theme.CorporateTheme.colors.background }}
                      />
                      <PxBadge>PX</PxBadge>
                    </NumberPxWrapper>
                  </FieldBlock>

                  

                  <FieldBlock>
                    <Label theme={theme}>Font Weight</Label>
                    <AMSSelect
                      staticOptions={fontWeightOptions}
                      value={fontWeightOptions.find(option => option.value === (tempSettings.paragraphFontWeight || 'regular')) || fontWeightOptions[0]}
                      onChange={(option) => {
                        const selectedOption = Array.isArray(option) ? option[0] : option;
                        if (selectedOption?.value) {
                          updateTempSetting('paragraphFontWeight', selectedOption.value);
                        }
                      }}
                      placeholder="Select font weight"
                      isSearchable={false}
                      isClearable={false}
                    />
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Line Height</Label>
                    <StyledInput
                      theme={theme}
                      $fontWeight="500"
                      color={theme.CorporateTheme.colors.headerColor}
                      value={tempSettings.paragraphLineHeight || ''}
                      onChange={(e) => updateTempSetting('paragraphLineHeight', e.target.value)}
                      placeholder="1.6"
                      style={{ background: theme.CorporateTheme.colors.background }}
                    />
                  </FieldBlock>
                </>
              )}

              {mode === 'divider' && (
                <>
                  <FieldBlock>
                    <Label theme={theme}>Color</Label>
                    <ColorRow>
                      <ColorInput
                        type="color"
                        value={tempSettings.separatorColor || '#BABDC1'}
                        onChange={(e) => updateTempSetting('separatorColor', e.target.value)}
                      />
                      <StyledInput
                        theme={theme}
                        $fontWeight="500"
                        color={theme.CorporateTheme.colors.headerColor}
                        value={tempSettings.separatorColor || ''}
                        onChange={(e) => updateTempSetting('separatorColor', e.target.value)}
                        placeholder="#BABDC1"
                        style={{ background: theme.CorporateTheme.colors.background }}
                      />
                    </ColorRow>
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Separator Type</Label>
                    <AMSSelect
                      staticOptions={separatorTypeOptions}
                      value={separatorTypeOptions.find(option => option.value === (tempSettings.separatorStyle || 'solid')) || separatorTypeOptions[0]}
                      onChange={(option) => {
                        const selectedOption = Array.isArray(option) ? option[0] : option;
                        if (selectedOption?.value) {
                          updateTempSetting('separatorStyle', selectedOption.value);
                        }
                      }}
                      placeholder="Select separator type"
                      isSearchable={false}
                      isClearable={false}
                    />
                  </FieldBlock>

                  <FieldBlock>
                    <Label theme={theme}>Separator Height</Label>
                    <NumberPxWrapper>
                      <StyledInput
                        theme={theme}
                        $fontWeight="500"
                        color={theme.CorporateTheme.colors.headerColor}
                        type="number"
                        value={tempSettings.separatorHeight ?? ''}
                        onChange={(e) => updateTempSetting('separatorHeight', e.target.value === '' ? '' : Number(e.target.value))}
                        placeholder="2"
                        min={0}
                        style={{ background: theme.CorporateTheme.colors.background }}
                      />
                      <PxBadge>PX</PxBadge>
                    </NumberPxWrapper>
                  </FieldBlock>
                </>
              )}

              <FooterActions>
                <Button
                  theme={theme}
                  btnBg={theme?.CorporateTheme?.colors?.background}
                  color={theme?.CorporateTheme?.colors?.textSecondary}
                  type="button"
                  $variant="customButton"
                  className="px-3"
                  onClick={onClose}
                  borderColor={theme?.CreateFormTheme?.colors?.inputBorders}
                >
                  <CrossIcon />
                  Cancel
                </Button>

                <Button
                  theme={theme}
                  btnBg={theme?.CorporateTheme?.colors?.primary}
                  color={theme?.CorporateTheme?.colors?.background}
                  type="button"
                  $variant="customButton"
                  className="px-3"
                  borderColor="none"
                  onClick={handleSave}
                >
                  <SaveIcon color={theme.CorporateTheme.colors.background} />
                  Save
                </Button>
              </FooterActions>
            </SidebarContent>
          </>
        )}
      </RightSidebar>
    </>
  );
};

export default FieldSettingsPanel;

const Backdrop = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 999;
`;

const RightSidebar = styled.div<{ isOpen: boolean, theme: Theme }>`
  position: fixed;
  right: ${props => props.isOpen ? '0' : '-534px'};
  top: 0;
  width: 534px;
  height: 100vh;
  background: ${props => props.theme.CorporateTheme.colors.background};
  border-left: 1px solid #e9ecef;
  overflow: hidden;
  transition: right 0.3s ease;
  display: flex;
  flex-direction: column;
  z-index: 100000;
  box-shadow: ${props => props.isOpen ? '-2px 0 8px rgba(0, 0, 0, 0.15)' : 'none'};
`;

export const SidebarHeaderWrapper = styled.div<{ $expanded?: boolean }>`
    background: ${({ theme }) => theme.CreateFormTheme?.colors.background};
    padding: 0 ${CreateFormTheme?.spacing.lg} ${CreateFormTheme?.spacing.lg};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;
`;

const FormTitleWrapper = styled.div`
  flex: 1;
  
  > div {
    height: auto;
    background: none !important;
  }
  
  h2 {
    margin: 0;
    padding: 20px;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  position: absolute;
  top: 15px;
  right: 15px;

  &:hover {
    background: #f8f9fa;
    color: #212529;
  }
`;

const SidebarContent = styled.div`
overflow-y: auto;
    margin:-20px 20px 20px 20px;
    display: flex;
    flex-direction: column; 
    border-radius: 8px;
    padding: 0px 20px 20px 20px;
    background-color: ${({ theme }) => theme.CorporateTheme?.colors?.background};
    border: 1px solid ${({ theme }) => theme.CorporateTheme?.colors?.border};
`;

const SectionTitle = styled(StyledText)`
  margin-top: 22px;
  margin-bottom: 12px;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 18px;
  flex: 1;
`;

const Label = styled(StyledText)`
  font-size: 14px;
  font-weight: 600;
`;

const ToggleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`;

const ToggleItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #344054;

  input {
    width: 16px;
    height: 16px;
  }
`;

const TextInput = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 14px;
  color: #344054;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #0b3d91;
    box-shadow: 0 0 0 2px rgba(11, 61, 145, 0.12);
  }
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 120px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 12px;
  font-size: 14px;
  color: #344054;
  background: #fff;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #0b3d91;
    box-shadow: 0 0 0 2px rgba(11, 61, 145, 0.12);
  }
`;

const TwoColumn = styled.div`
  display: flex;
  gap: 12px;
  align-items: flex-start;
  flex-wrap: nowrap;
`;

const ColorRow = styled.div`
  display: grid;
  grid-template-columns: 105px 1fr;
  gap: 10px;
  align-items: center;
`;

const ColorInput = styled.input`
  width: 105px;
  height: 42px;
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  padding: 0;
  cursor: pointer;
`;

const LevelRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0;
`;

const NumberPxWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const NumberInput = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 0 56px 0 12px; /* reserve space for PX badge */
  font-size: 14px;
  color: #344054;
  background: #fff;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  &:focus {
    outline: none;
    border-color: #0b3d91;
    box-shadow: 0 0 0 2px rgba(11, 61, 145, 0.12);
  }
`;

const PxBadge = styled.div`
  position: absolute;
  right: 0px;
  top: 50%;
  transform: translateY(-50%);
  background: #f6f7f9;
  border: 1px solid #d0d5dd;
  height: 42px;
  padding: 0 10px;
  display: flex;
  align-items: center;
  border-top-right-radius: 6px;
  border-bottom-right-radius: 6px;
  font-size: 13px;
  color: #344054;
  pointer-events: none;
`;

const LevelButton = styled.button<{ theme: Theme; $selected?: boolean }>`
  flex: 1;
  min-width: 54px;
  padding: 10px 12px;
  border: 1px solid ${({ theme, $selected }) => $selected ? theme.CorporateTheme.colors.accentOrange : '#d0d5dd'};
  background: ${({ theme, $selected }) => $selected ? '#fff4ee' : theme.CorporateTheme.colors.background};
  color: ${({ theme, $selected }) => $selected ? theme.CorporateTheme.colors.accentOrange : theme.CorporateTheme.colors.textPrimary};
  font-size: 13px;
  font-weight: 600;
  margin-left: -1px;

  &:first-child {
    margin-left: 0;
    border-top-left-radius: 8px;
    border-bottom-left-radius: 8px;
  }

  &:last-child {
    border-top-right-radius: 8px;
    border-bottom-right-radius: 8px;
  }
`;

const AlignmentRow = styled.div`
  display: flex;
  gap: 12px;
`;

const AlignmentOption = styled.button<{ theme: Theme; $selected?: boolean }>`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 12px;
  border: 1px solid ${({ theme, $selected }) => $selected ? theme.CorporateTheme.colors.primary : '#d0d5dd'};
  border:none;
  background: none;
  cursor: pointer;
`;

const AlignmentIconBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewBox = styled.div<{ theme: Theme }>`
  margin-top: 24px;
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.CorporateTheme.colors.border};
  border-radius: 10px;
  background: ${({ theme }) => theme.CorporateTheme.colors.background};
`;

const PreviewHeading = styled.h1<{
  theme: Theme;
  $align: string;
  $color: string;
  $fontSize: string;
  $fontWeight: string;
}>`
  margin: 0;
  color: ${({ $color }) => $color};
  font-size: ${({ $fontSize }) => $fontSize};
  line-height: 1.15;
  font-weight: ${({ $fontWeight }) => ($fontWeight === 'regular' ? 400 : $fontWeight === 'medium' ? 500 : 700)};
  text-align: ${({ $align }) => $align};
  word-break: break-word;
`;

const PreviewParagraph = styled.p<{
  theme: Theme;
  $align: string;
  $color: string;
  $fontSize: string;
  $fontWeight: string;
  $lineHeight: string;
}>`
  margin: 0;
  color: ${({ $color }) => $color};
  font-size: ${({ $fontSize }) => $fontSize};
  line-height: ${({ $lineHeight }) => $lineHeight};
  font-weight: ${({ $fontWeight }) => ($fontWeight === 'regular' ? 400 : $fontWeight === 'medium' ? 500 : 700)};
  text-align: ${({ $align }) => $align};
  white-space: pre-wrap;
  word-break: break-word;
  a {
    pointer-events: none;
  }
`;

const PreviewDivider = styled.div<{
  $color: string;
  $height: string;
  $style: string;
  $width: string;
  $spacing: string;
}>`
  width: ${({ $width }) => $width};
  height: ${({ $height }) => $height};
  margin: ${({ $spacing }) => $spacing};
  border-radius: 999px;
  background: ${({ $color }) => $color};
  border-style: ${({ $style }) => $style};
`;

const FooterActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
`;